import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router';

import styles from './styles.module.css';
import { GaugeData } from '../GlobalViewContainer/types';
import AlarmsTab from './tabs/AlarmsTab';
import AlarmSubscriptionTab from './tabs/AlarmSubscriptionTab';
import ControlTab from './tabs/ControlTab';
import EnclosureTab from './tabs/EnclosureTab';
import GeneratorTab from './tabs/GeneratorTab';
import LubeTab from './tabs/LubeTab';
import SummaryTab from './tabs/SummaryTab';
import TurbineTab from './tabs/TurbineTab';
import VibrationBearingsTab from './tabs/VibrationBearingsTab';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import Tabs, { Tab } from '@/components/Tabs';
import WebSocketIndicator from '@/components/WebSocketIndicator';
import { socketsEntities } from '@/constants/navigationPaths';
import { PermissionsList } from '@/constants/permissionsList';
import { turbinesTabs } from '@/constants/turbines';
import { useNavigationBar } from '@/context/NavigationBarContext';
import { useToast } from '@/context/ToastContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { useGetTurbineDetails } from '@/hooks/services/Turbines';
import useShouldRefresh from '@/hooks/useShouldRefresh';
import {
  saveTurbineId,
  saveTurbineName
} from '@/slices/installedProductsSlice';
import { AlarmData } from '@/slices/types';
import { installedProductsKeys } from '@/solaris-lens-common-lib/src/constants/queryKeys/InstalledProducts';
import {
  getInstalledProduct,
  getHistoricalAlarm
} from '@/solaris-lens-common-lib/src/services/installedProducts';
import delay from '@/solaris-lens-common-lib/src/utils';
import { mergeWithTurbineSocketData } from '@/solaris-lens-common-lib/src/utils/turbines';
import { getTurbineDataPath } from '@/solaris-lens-common-lib/src/utils/turbinesPaths';
import { RootState } from '@/store';
import {
  buildTurbineBreadcrumb,
  buildTurbineQueryParamsFromSearchParams,
  TurbineFromLocationState
} from '@/utils/turbineNavigation';

interface InstalledProductResponse {
  data: {
    info?: {
      alarms?: any[];
      shutdowns?: any[];
    };
  };
  info?: {
    alarms?: any[];
    shutdowns?: any[];
  };
  meta?: any;
}

interface HistoricalAlarmResponse {
  data: AlarmData[] | { alarms?: AlarmData[] };
  info?: any;
}

export default function TurbineViewContainer() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;
  useShouldRefresh();
  const { setTabsSegment, setActiveTabId, activeTabId, setBreadcrumbOverride } =
    useNavigationBar();
  const [installedProductsState, setInstalledProductsState] = useState<
    GaugeData[]
  >([]);

  const {
    isLoading,
    error,
    data: turbineResponse
  } = useGetTurbineDetails(id || '', {
    useQueryOptions: {
      enabled: Boolean(id)
    }
  });

  // State to hold turbineResponse that can be updated with WebSocket data
  const [currentTurbineResponse, setCurrentTurbineResponse] =
    useState<any>(turbineResponse);

  useEffect(() => {
    // Update currentTurbineResponse when turbineResponse changes
    if (turbineResponse) {
      setCurrentTurbineResponse(turbineResponse);
    }
  }, [turbineResponse]);

  useEffect(() => {
    const turbineData = getTurbineDataPath(currentTurbineResponse);
    if (!turbineData) return;
    setInstalledProductsState(turbineData);
  }, [currentTurbineResponse]);

  const fromLocation = routerLocation.state?.fromLocation as
    | TurbineFromLocationState
    | undefined;

  useEffect(() => {
    const turbineName = searchParams.get('name');

    setBreadcrumbOverride(
      buildTurbineBreadcrumb({
        turbineName: turbineName || id || '',
        fromLocation
      })
    );

    if (turbineName) {
      dispatch(saveTurbineName({ turbineName }));
    }
    if (id) {
      dispatch(saveTurbineId({ turbineId: id }));
    }

    return () => {
      setBreadcrumbOverride(null);
    };
  }, [
    searchParams,
    dispatch,
    id,
    fromLocation,
    setBreadcrumbOverride,
    routerLocation.pathname
  ]);

  const allowedTabs = useMemo(() => {
    const turbinePermissions = auditableElements?.TURBINE_DETAILS || {};

    const tabPermissionMap: Record<string, keyof typeof turbinePermissions> = {
      summary: 'SUMMARY',
      control: 'CONTROL',
      generator: 'GENERATOR',
      turbine: 'TURBINE',
      vibrationBearings: 'VIBRATION_BEARINGS',
      lube: 'LUBE',
      enclosure: 'ENCLOSURE',
      alarms: 'ALARMS_SHUTDOWNS',
      alarmSubscription: 'ALARM_SUBSCRIPTION'
    };

    return turbinesTabs.filter(tabItem => {
      if (tabItem.id === 'alarmSubscription') {
        return true;
      }
      const permissionKey = tabPermissionMap[tabItem.id];
      return permissionKey ? turbinePermissions[permissionKey] === true : false;
    });
  }, [auditableElements]);

  useEffect(() => {
    const tabsWithCallbacks = allowedTabs.map(tabItem => ({
      ...tabItem,
      onClick: (tabId: string) => {
        setActiveTabId(tabId);
        const urlParams = buildTurbineQueryParamsFromSearchParams(searchParams);
        navigate(`/turbines/${id}/${tabId}${urlParams}`, {
          replace: true,
          state: routerLocation.state
        });
      }
    }));
    setTabsSegment(tabsWithCallbacks);

    const validTab =
      tab && allowedTabs.some(t => t.id === tab)
        ? tab
        : allowedTabs[0]?.id || 'summary';
    setActiveTabId(validTab);

    if (!tab && validTab && id) {
      const urlParams = buildTurbineQueryParamsFromSearchParams(searchParams);
      navigate(`/turbines/${id}/${validTab}${urlParams}`, {
        replace: true,
        state: routerLocation.state
      });
    }

    return () => {
      setTabsSegment([]);
      setActiveTabId('');
    };
  }, [
    setTabsSegment,
    setActiveTabId,
    tab,
    id,
    navigate,
    searchParams,
    allowedTabs,
    routerLocation.state
  ]);

  const { setEntityData, socketData } = useWebSocket();

  useEffect(() => {
    if (isLoading || installedProductsState.length === 0) return;

    delay(1000).then(() => {
      setEntityData({
        entityName: socketsEntities.pulseTurbineSummary,
        entityId: id || ''
      });
    });
  }, [installedProductsState.length]);

  useEffect(() => {
    return () => {
      setEntityData({ entityName: '', entityId: '' });
    };
  }, [setEntityData]);

  useEffect(() => {
    if (socketData && currentTurbineResponse) {
      // Update installedProductsState with WebSocket data
      const data = mergeWithTurbineSocketData({
        turbineData: installedProductsState,
        turbineSocket: socketData
      });

      setInstalledProductsState(data);

      // Also update currentTurbineResponse if socketData contains alarms/shutdowns info
      // This ensures alarms tab receives real-time updates
      if (socketData[0]?.info) {
        setCurrentTurbineResponse((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              info: {
                ...prev.data?.info,
                ...socketData[0].info
              }
            }
          };
        });
      }
    }
  }, [socketData, currentTurbineResponse]);

  const [historyStartDate, setHistoryStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString();
  });
  const [historyEndDate, setHistoryEndDate] = useState<string>(() =>
    new Date().toISOString()
  );

  const prevDatesRef = useRef<{ startDate: string; endDate: string }>({
    startDate: historyStartDate,
    endDate: historyEndDate
  });

  // Build installedProductData from currentTurbineResponse (which includes WebSocket updates)
  // This ensures alarms tab receives real-time updates like other tabs
  const installedProductDataFromResponse = useMemo(() => {
    if (currentTurbineResponse?.data?.info) {
      return {
        data: currentTurbineResponse.data,
        info: currentTurbineResponse.data.info,
        meta: currentTurbineResponse.meta
      } as InstalledProductResponse;
    }
    return undefined;
  }, [currentTurbineResponse]);

  // Only fetch installedProduct separately if we don't have it from turbineResponse
  const shouldFetchInstalledProduct =
    activeTabId === 'alarms' && !installedProductDataFromResponse;

  const queryKey = useMemo(
    () => installedProductsKeys.detailById(id || '', token),
    [id, token]
  );

  const { data: installedProductDataFromQuery, error: installedProductError } =
    useQuery<InstalledProductResponse>({
      queryKey,
      queryFn: async () => {
        if (!token || !id) throw new Error('Missing token or id');
        return await getInstalledProduct(token, id, null);
      },
      enabled: Boolean(token && id && shouldFetchInstalledProduct),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    });

  const installedProductData =
    installedProductDataFromResponse || installedProductDataFromQuery;

  const queryClient = useQueryClient();

  const historyQueryKey = useMemo(
    () => ['historicalAlarms', id, token, historyStartDate, historyEndDate],
    [id, token, historyStartDate, historyEndDate]
  );

  const tabs: Tab[] = [
    { id: 'alarms', label: 'ALARMS' },
    { id: 'historyLog', label: 'HISTORY LOG' }
  ];

  const [activeAlarmsTabId, setActiveAlarmsTabId] = useState<string>(
    tabs[0].id
  );
  const handleAlarmsTabChange = (tabId: string) => {
    setActiveAlarmsTabId(tabId);
  };

  const {
    data: historyData,
    isLoading: isLoadingHistory,
    error: historyError
  } = useQuery<HistoricalAlarmResponse>({
    queryKey: historyQueryKey,
    queryFn: async () => {
      if (!token || !id) throw new Error('Missing token or id');
      return await getHistoricalAlarm({
        token,
        id,
        startDate: historyStartDate,
        endDate: historyEndDate,
        applyDateFormat: true
      });
    },
    enabled: Boolean(token && id && activeTabId === 'alarms'),
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  useEffect(() => {
    prevDatesRef.current = {
      startDate: historyStartDate,
      endDate: historyEndDate
    };
  }, [historyStartDate, historyEndDate]);

  const handleHistoryFilterChange = async (
    startDate: string,
    endDate: string
  ) => {
    setHistoryStartDate(startDate);
    setHistoryEndDate(endDate);

    await queryClient.invalidateQueries({
      queryKey: ['historicalAlarms', id, token],
      exact: false
    });

    await queryClient.refetchQueries({
      queryKey: ['historicalAlarms', id, token, startDate, endDate],
      exact: true
    });
  };

  useEffect(() => {
    if (error) {
      showToast('Unable to load turbine details. Please try again.', {
        type: 'error',
        autoClose: 5000
      });
    }
    if (historyError) {
      showToast('Unable to load history data. Please try again.', {
        type: 'error',
        autoClose: 5000
      });
    }
  }, [error, historyError, showToast]);

  const renderTabContent = () => {
    const currentTab = activeTabId || 'summary';
    switch (currentTab) {
      case 'summary':
        return (
          <SummaryTab
            turbineData={installedProductsState}
            turbineStatus={{
              powerStatus: currentTurbineResponse?.data?.powerStatus,
              updatedAt: currentTurbineResponse?.data.updatedAt
            }}
          />
        );
      case 'control':
        return <ControlTab turbineData={installedProductsState} />;
      case 'generator':
        return <GeneratorTab turbineData={installedProductsState} />;
      case 'turbine':
        return <TurbineTab turbineData={installedProductsState} />;
      case 'vibrationBearings':
        return <VibrationBearingsTab turbineData={installedProductsState} />;
      case 'lube':
        return <LubeTab turbineData={installedProductsState} />;
      case 'enclosure':
        return <EnclosureTab turbineData={installedProductsState} />;
      case 'alarms': {
        const historyDataArray = (() => {
          if (!historyData?.data) return undefined;
          const data = historyData.data;
          if (Array.isArray(data)) {
            return data;
          }
          if (
            typeof data === 'object' &&
            'alarms' in data &&
            Array.isArray((data as any).alarms)
          ) {
            return (data as any).alarms;
          }
          return undefined;
        })();

        return (
          <AlarmsTab
            installedProductData={installedProductData}
            historyData={historyDataArray}
            isLoadingHistory={isLoadingHistory}
            historyError={historyError as Error | null}
            installedProductError={installedProductError as Error | null}
            onHistoryFilterChange={handleHistoryFilterChange}
            turbineId={id || ''}
            activeAlarmsTabId={activeAlarmsTabId}
          />
        );
      }
      case 'alarmSubscription':
        return <AlarmSubscriptionTab />;
      default:
        return <SummaryTab />;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.mainContainer}>
        <ContainerLoader />
      </div>
    );
  }

  return (
    <>
      {activeTabId !== 'summary' && activeTabId !== 'alarmSubscription' && (
        <div className={styles.wsIndicator}>
          {activeTabId === 'alarms' && (
            <div className={styles.tabsWrapper}>
              <Tabs
                tabs={tabs}
                activeTabId={activeAlarmsTabId}
                onTabChange={handleAlarmsTabChange}
                secondaryTabs={true}
              />
            </div>
          )}
          <WebSocketIndicator />
        </div>
      )}
      <div
        className={
          activeTabId === 'alarms'
            ? styles.mainContainerAlarms
            : styles.mainContainer
        }
      >
        <div className={styles.contentContainer}>{renderTabContent()}</div>
      </div>
    </>
  );
}
